import React from 'react';
import { fetchRecentRecords } from '../../api/recentRecords';
import { useApiBase } from '../../hooks/useApiBase';
import type { RecentRecordEntry } from '../../types/recentRecords';
import { formatHstRelativeTime } from '../../utils/hstDateFormat';
import { Button } from '../ui/button';
import { MobileFormPanel } from './MobileFormPanel';
import { useMobileNavigate } from './MobileNavigationGuard';
import { mobileDestinationMessage, resolveMobileDestination } from './navigation';

export function MobileRecentRecords({ onClose, onNavigate, onRestoreFocus }: { onClose: () => void; onNavigate: () => void; onRestoreFocus: () => void }) {
  const apiBase = useApiBase();
  const navigate = useMobileNavigate();
  const [records, setRecords] = React.useState<RecentRecordEntry[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<{ key: string; message: string } | null>(null);
  const [version, setVersion] = React.useState(0);
  React.useEffect(() => {
    const controller = new AbortController();
    setLoading(true); setError(null);
    void fetchRecentRecords(apiBase, { limit: 25, signal: controller.signal }).then((items) => {
      if (!controller.signal.aborted) setRecords(items.slice(0, 25));
    }).catch(() => {
      if (!controller.signal.aborted) setError('Unable to load recent records. Try again.');
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, [apiBase, version]);
  return <MobileFormPanel title="Recent Records" description="Recently viewed records from your phone and desktop." onClose={onClose} onRestoreFocus={onRestoreFocus} footer={<><Button variant="outline" onClick={onClose}>Close</Button><Button disabled={loading} onClick={() => setVersion((value) => value + 1)}>{loading ? 'Loading…' : error ? 'Retry' : 'Refresh'}</Button></>}>
    {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
    {loading && !records.length && <p role="status">Loading recent records…</p>}
    {!loading && !error && !records.length && <p>Records you open will appear here.</p>}
    <div className="space-y-2">{records.map((record) => {
      const target = record.route?.trim() || '';
      const destination = target ? resolveMobileDestination(target) : 'unknown';
      const timestamp = record.lastViewedAt && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(record.lastViewedAt) ? `${record.lastViewedAt}Z` : record.lastViewedAt;
      return <button key={`${record.recordType}:${record.recordId}`} type="button" className="w-full min-h-11 rounded-xl border border-border p-3 text-left whitespace-normal break-words" onClick={() => {
        if (destination !== 'supported') { setNotice({ key: `${record.recordType}:${record.recordId}`, message: mobileDestinationMessage(target) }); return; }
        navigate(target); onNavigate();
      }}>
        <span className="block font-semibold">{record.label || record.recordId}</span>
        <span className="block text-sm text-muted-foreground">{record.recordType.replace(/_/g, ' ')} · {timestamp && Number.isFinite(Date.parse(timestamp)) ? formatHstRelativeTime(timestamp) : 'Recently viewed'}</span>
        {notice?.key === `${record.recordType}:${record.recordId}` && <span role="status" className="block mt-2 text-sm text-muted-foreground">{notice.message}</span>}
        {destination !== 'supported' && <span className="block text-sm text-muted-foreground">{destination === 'desktop' ? 'Desktop only' : 'Unavailable destination'}</span>}
      </button>;
    })}</div>
  </MobileFormPanel>;
}
